import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { toast } from 'react-toastify';
import { supplierOrderChangeAPI } from '../../services/supplierOrderChange';
import { usePendingApprovals } from '../../context/PendingApprovals';

/**
 * #120207 — Màn duyệt thay đổi đơn hàng do NCC gửi từ link Zalo.
 *
 * Quyền thật do backend chốt (`SupplierOrderChangeService`): ADMIN duyệt mọi chi nhánh, MANAGER
 * chỉ duyệt đơn của đúng chi nhánh mình — MỘT TRONG HAI là đủ, đúng mẫu ImportRequest.
 */

const fmtQty = (n) =>
  n == null ? '—' : Number(n).toLocaleString('vi-VN', { maximumFractionDigits: 3 });
const fmtMoney = (n) => (n == null ? '—' : Number(n).toLocaleString('vi-VN') + 'đ');
const fmtDate = (d) => (d ? new Date(d).toLocaleString('vi-VN') : '—');

const STATUS = {
  PENDING: { label: 'Chờ duyệt', bg: 'warning' },
  APPROVED: { label: 'Đã duyệt', bg: 'success' },
  REJECTED: { label: 'Đã từ chối', bg: 'danger' },
};

const ACTION_LABEL = {
  UPDATE_QUANTITY: { label: 'Sửa số lượng', bg: 'primary' },
  PARTIAL: { label: 'Không đủ hàng', bg: 'warning' },
  OUT_OF_STOCK: { label: 'Hết hàng', bg: 'danger' },
  REMOVE_LINE: { label: 'Bỏ khỏi đơn', bg: 'danger' },
};

export default function SupplierOrderChangeReviewPage() {
  // #231533 — badge sidebar/trang chủ phải rơi NGAY khi duyệt/từ chối xong, không đợi hết 30 giây
  // của vòng lặp định kỳ: người vừa bấm duyệt mà số vẫn còn đó sẽ tưởng thao tác không ăn.
  const { refresh: refreshPendingApprovals } = usePendingApprovals();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const fetchList = async () => {
    try {
      setLoading(true);
      const data = await supplierOrderChangeAPI.list(
        statusFilter ? { status: statusFilter } : {},
      );
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || 'Không tải được danh sách đề xuất');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openDetail = async (id) => {
    try {
      setDetailLoading(true);
      setDetail(await supplierOrderChangeAPI.getDetail(id));
    } catch (err) {
      toast.error(err?.message || 'Không tải được chi tiết đề xuất');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActing(true);
      await supplierOrderChangeAPI.approve(detail.id);
      toast.success('Đã duyệt. Hệ thống đang bắn lại tin xác nhận cho NCC qua Zalo.');
      setDetail(null);
      fetchList();
      refreshPendingApprovals();
    } catch (err) {
      toast.error(err?.message || 'Duyệt thất bại');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    try {
      setActing(true);
      await supplierOrderChangeAPI.reject(detail.id, rejectReason);
      toast.success('Đã từ chối. Đơn giữ nguyên như cũ, NCC sẽ được báo qua Zalo.');
      setShowReject(false);
      setRejectReason('');
      setDetail(null);
      fetchList();
      refreshPendingApprovals();
    } catch (err) {
      toast.error(err?.message || 'Từ chối thất bại');
    } finally {
      setActing(false);
    }
  };

  return (
    <PageLayout title="Duyệt thay đổi đơn NCC">
      <Row>
        <Col xl={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h5 className="mb-1">Đề xuất thay đổi đơn hàng từ NCC</h5>
                <small className="text-muted">
                  NCC gửi từ link trong tin Zalo. Duyệt mới áp thay đổi thật vào đơn.
                </small>
              </div>
              <Form.Select
                style={{ width: 190 }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="PENDING">Chờ duyệt</option>
                <option value="APPROVED">Đã duyệt</option>
                <option value="REJECTED">Đã từ chối</option>
                <option value="">Tất cả</option>
              </Form.Select>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
              ) : !list.length ? (
                <Alert variant="light" className="mb-0 text-center">
                  Không có đề xuất nào ở trạng thái này.
                </Alert>
              ) : (
                <Table responsive hover className="align-middle">
                  <thead>
                    <tr>
                      <th>Đơn hàng</th>
                      <th>Chi nhánh</th>
                      <th>NCC</th>
                      <th className="text-center">Nội dung</th>
                      <th>NCC gửi lúc</th>
                      <th className="text-center">Trạng thái</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => (
                      <tr key={r.id}>
                        <td className="fw-semibold">{r.purchase_order?.order_number}</td>
                        <td>{r.purchase_order?.branch?.name}</td>
                        <td>{r.supplier?.name}</td>
                        <td className="text-center">
                          {r.cancel_order ? (
                            <Badge bg="danger">Xin huỷ cả đơn</Badge>
                          ) : (
                            <>
                              <Badge bg="secondary" className="me-1">
                                {r._count?.items ?? 0} dòng sửa
                              </Badge>
                              {!!r._count?.material_proposals && (
                                <Badge bg="info">
                                  {r._count.material_proposals} NVL thay thế
                                </Badge>
                              )}
                            </>
                          )}
                        </td>
                        <td>{fmtDate(r.created_at)}</td>
                        <td className="text-center">
                          <Badge bg={STATUS[r.status]?.bg}>{STATUS[r.status]?.label}</Badge>
                        </td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-primary" onClick={() => openDetail(r.id)}>
                            Xem
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={!!detail || detailLoading} onHide={() => setDetail(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            Đề xuất thay đổi — {detail?.purchase_order?.order_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading || !detail ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <div><small className="text-muted">Chi nhánh</small></div>
                  <div className="fw-semibold">{detail.purchase_order?.branch?.name}</div>
                </Col>
                <Col md={6}>
                  <div><small className="text-muted">Nhà cung cấp</small></div>
                  <div className="fw-semibold">{detail.supplier?.name}</div>
                </Col>
              </Row>

              {detail.cancel_order && (
                <Alert variant="danger">
                  <b>NCC xin huỷ TOÀN BỘ đơn hàng này.</b> Duyệt sẽ chuyển đơn sang trạng thái Đã huỷ.
                </Alert>
              )}

              {!!detail.supplier_note && (
                <Alert variant="light">
                  <small className="text-muted d-block">Lời nhắn của NCC</small>
                  {detail.supplier_note}
                </Alert>
              )}

              {!detail.cancel_order && (
                <>
                  <h6 className="mb-2">So sánh từng dòng</h6>
                  <Table responsive bordered size="sm" className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>NVL</th>
                        <th className="text-end">Đơn gốc</th>
                        <th className="text-center">NCC đề xuất</th>
                        <th className="text-end">SL mới</th>
                        <th>Ghi chú NCC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.comparison?.map((c) => (
                        <tr key={c.purchase_order_item_id} className={c.changed ? '' : 'text-muted'}>
                          <td>{c.material_name}</td>
                          <td className="text-end">
                            {fmtQty(c.original_quantity)} {c.package_unit_label || c.package_name || ''}
                          </td>
                          <td className="text-center">
                            {c.action ? (
                              <Badge bg={ACTION_LABEL[c.action]?.bg}>
                                {ACTION_LABEL[c.action]?.label}
                              </Badge>
                            ) : (
                              <span className="text-muted">Giữ nguyên</span>
                            )}
                          </td>
                          <td className="text-end fw-semibold">
                            {c.action === 'OUT_OF_STOCK' || c.action === 'REMOVE_LINE'
                              ? '0 (bỏ dòng)'
                              : fmtQty(c.proposed_quantity ?? c.original_quantity)}
                          </td>
                          <td><small>{c.supplier_note || '—'}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {!!detail.material_proposals?.length && (
                <>
                  <h6 className="mb-2 mt-3">NVL thay thế NCC đề xuất</h6>
                  {detail.material_proposals.map((p) => (
                    <Card key={p.id} className="mb-2">
                      <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="fw-semibold">{p.material_name}</div>
                          <Badge bg={STATUS[p.status]?.bg}>{STATUS[p.status]?.label}</Badge>
                        </div>
                        <div className="small text-muted mt-1">
                          Đơn vị: <b>{p.material_unit}</b> · Quy cách: <b>{p.package_name}</b>
                          {p.package_unit_label ? ` (${p.package_unit_label})` : ''} ={' '}
                          <b>{fmtQty(p.package_quantity_in_base)} {p.material_unit}</b>
                        </div>
                        <div className="small text-muted">
                          Giá đề xuất: <b>{fmtMoney(p.proposed_price)}</b> · SL tối thiểu đặt:{' '}
                          <b>{fmtQty(p.min_order_quantity)}</b>
                        </div>
                        {p.reason && (
                          <div className="small mt-1">Lý do thay thế: {p.reason}</div>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                  {detail.status === 'PENDING' && (
                    <Alert variant="warning" className="py-2 small mb-0">
                      Duyệt sẽ tạo thật các NVL trên vào danh mục và gắn vào NCC này.
                    </Alert>
                  )}
                </>
              )}

              <hr />
              <div className="small text-muted">
                NCC gửi lúc {fmtDate(detail.created_at)} · link{' '}
                <code>{(detail.submitted_token || '').slice(0, 12)}…</code> · IP {detail.submitted_ip || '—'}
                {detail.reviewer && (
                  <> · {STATUS[detail.status]?.label} bởi <b>{detail.reviewer.full_name}</b> lúc{' '}
                    {fmtDate(detail.reviewed_at)}</>
                )}
                {detail.rejected_reason && <> · Lý do: {detail.rejected_reason}</>}
              </div>
            </>
          )}
        </Modal.Body>
        {detail?.status === 'PENDING' && (
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setDetail(null)} disabled={acting}>
              Đóng
            </Button>
            <Button variant="outline-danger" onClick={() => setShowReject(true)} disabled={acting}>
              Từ chối
            </Button>
            <Button variant="success" onClick={handleApprove} disabled={acting}>
              {acting ? 'Đang xử lý…' : 'Duyệt & báo NCC'}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal show={showReject} onHide={() => setShowReject(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Từ chối đề xuất</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            Đơn hàng sẽ giữ nguyên như cũ. NCC được báo qua Zalo, <b>không kèm</b> lý do bên dưới
            (lý do chỉ lưu nội bộ).
          </p>
          <Form.Control
            as="textarea"
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Lý do từ chối (nội bộ)"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowReject(false)} disabled={acting}>
            Huỷ
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={acting}>
            {acting ? 'Đang xử lý…' : 'Xác nhận từ chối'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}
